import { captureException } from '@sentry/react';
import axios from 'axios';
import { loadTimeTracker } from '../utils/custom-hooks/useLoadTimeTracker';

const UNAUTHORIZED_ERROR_CODE = 401;
const VALIDATION_ERROR_CODE = 422;
const DONT_REPORT_CODES = [UNAUTHORIZED_ERROR_CODE, VALIDATION_ERROR_CODE];

export const headers = {
  'Content-Type': 'application/x-www-form-urlencoded',
  authorization: 'Basic NmMwOGViNGYxMTE4NGVmNGFkZTM2YTFiYjgyMjAwNDE6'
};

export const request = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: { 'Cache-Control': 'no-cache' }
});

request.interceptors.request.use(
  (config) => {
    const url = config.url;

    if (url.includes('/v2/orgs?attributes=workspaces&include_extended=true&workspaces=')) {
      loadTimeTracker(true, 'my-organizations', false);
    }
    if (url.includes('region-tree?reference_date=')) {
      loadTimeTracker(true, 'landing-organization (fields)', false);
    }
    if (url.includes('/properties?attributes=total_area')) {
      loadTimeTracker(true, 'landing-organization (farms)', false);
    }
    if (url.includes('v2/roles')) {
      loadTimeTracker(true, 'edit member', false);
    }
    if (url.includes('/v2/accounts/batch/invitation')) {
      loadTimeTracker(true, 'invite member', false);
    }

    return config;
  },
  (error) => {
    const url = error.config?.url || 'unknown-url';
    const method = error.config?.method?.toUpperCase() || 'UNKNOWN';

    const customError = new Error(`Request Error on [${method}] ${url} – ${error.message}`);
    customError.name = 'HttpRequestError';

    captureException(customError, {
      tags: {
        phase: 'request',
        requestUrl: url,
        requestMethod: method
      },
      extra: {
        originalMessage: error.message,
        requestConfig: error.config,
        file: 'api.config.ts',
        interceptedIn: 'request'
      },
      fingerprint: ['{{ default }}', 'request', url]
    });

    return Promise.reject(error);
  }
);

// Interceptor should filter errors 401 and 422
// and send to Sentry only relevant errors
request.interceptors.response.use(
  (response) => {
    const url = response.config.url;

    if (url.includes('/v2/orgs?attributes=workspaces&include_extended=true&workspaces=')) {
      loadTimeTracker(false, 'my-organizations', false);
    }
    if (url.includes('region-tree?reference_date=')) {
      loadTimeTracker(false, 'landing-organization (fields)', false);
    }
    if (url.includes('/properties?attributes=total_area')) {
      loadTimeTracker(false, 'landing-organization (farms)', false);
    }
    if (url.includes('v2/roles')) {
      loadTimeTracker(false, 'edit member', false);
    }
    if (url.includes('/v2/accounts/batch/invitation')) {
      loadTimeTracker(false, 'invite member', false);
    }

    return response;
  },
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url;
    const method = error.config?.method?.toUpperCase();
    const apiMessage = error.response?.data?.message || 'No API message';

    if (DONT_REPORT_CODES.includes(status)) {
      return Promise.reject(error);
    }

    const customError = new Error(`HTTP ${status} on [${method}] ${url} – ${apiMessage}`);
    customError.name = `HttpError_${status}`;

    captureException(customError, {
      tags: {
        httpStatus: String(status),
        requestUrl: url || '',
        requestMethod: method || ''
      },
      extra: {
        apiMessage,
        responseData: error.response?.data,
        file: 'api.config.ts',
        interceptedIn: 'response'
      },
      fingerprint: ['{{ default }}', String(status), url || 'unknown-url']
    });

    return Promise.reject(error);
  }
);
